// Sanctum SPA cookie auth. The session cookie is set by the central
// auth app at the .internetsociology.org domain, so a single sign-in
// covers varilib + socialarchive. We never store a bearer token.
//
// Login UI lives at https://auth.internetsociology.org. On any 401 we
// redirect there with a return_to so the user lands back here after
// signing in.

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const ORIGIN_BASE = API_BASE.replace(/\/api\/?$/, '');
const AUTH_ORIGIN = 'https://auth.internetsociology.org';
const STATE_CHANGING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

let csrfPrimed = false;

async function primeCsrf(): Promise<void> {
  if (csrfPrimed) return;
  await fetch(`${ORIGIN_BASE}/sanctum/csrf-cookie`, { credentials: 'include' });
  csrfPrimed = true;
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

/** Send the browser to the central login app, asking it to bounce back
 *  to where the user was when this fired. */
export function redirectToLogin(): void {
  const returnTo = encodeURIComponent(window.location.href);
  window.location.href = `${AUTH_ORIGIN}/login?return_to=${returnTo}`;
}

/** Hit the local logout endpoint to invalidate the shared session, then
 *  sweep the user to the apex marketing site rather than leaving them
 *  on a now-unauthenticated page that would just bounce them back to
 *  the auth app. Fire-and-forget; localStorage is cleared first so the
 *  UI doesn't briefly render a stale username. */
export function logout(): void {
  localStorage.removeItem('varilib_volunteer');
  fetch(`${API_BASE}/auth/logout`, {
    method: 'POST',
    credentials: 'include',
    headers: (() => {
      const h: Record<string, string> = { Accept: 'application/json' };
      const xsrf = readCookie('XSRF-TOKEN');
      if (xsrf) h['X-XSRF-TOKEN'] = xsrf;
      return h;
    })(),
  })
    .catch(() => { /* ignore */ })
    .finally(() => { window.location.href = 'https://internetsociology.org/'; });
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const method = (options?.method ?? 'GET').toUpperCase();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...(options?.headers as Record<string, string> | undefined),
  };

  if (STATE_CHANGING.has(method)) {
    await primeCsrf();
    const xsrf = readCookie('XSRF-TOKEN');
    if (xsrf) headers['X-XSRF-TOKEN'] = xsrf;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (response.status === 401) {
    localStorage.removeItem('varilib_volunteer');
    redirectToLogin();
    throw new Error('Session expired. Redirecting to sign in.');
  }

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const message = body?.message || `API error: ${response.status} ${response.statusText}`;
    throw new Error(message);
  }

  return response.json();
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T>(path: string) =>
    request<T>(path, { method: 'DELETE' }),
  upload: async <T>(path: string, formData: FormData) => {
    await primeCsrf();
    const headers: Record<string, string> = { Accept: 'application/json' };
    const xsrf = readCookie('XSRF-TOKEN');
    if (xsrf) headers['X-XSRF-TOKEN'] = xsrf;
    const res = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: formData,
    });
    if (res.status === 401) {
      localStorage.removeItem('varilib_volunteer');
      redirectToLogin();
      throw new Error('Session expired. Redirecting to sign in.');
    }
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new Error(body?.message || `API error: ${res.status}`);
    }
    return res.json() as Promise<T>;
  },
};
